import { ArrowRight, FileText } from 'lucide-react';
import type { FpQaHubResource } from './navigator-results.types';

type KeyResourcesPanelProps = {
  specialtyResources: FpQaHubResource[];
  careStageResources: FpQaHubResource[];
};

/**
 * The highlighted card follows the active specialist tab. The secondary list
 * stays care-stage based so the FP keeps the broader context while clicking
 * between specialist categories.
 */
export const KeyResourcesPanel = ({
  specialtyResources,
  careStageResources,
}: KeyResourcesPanelProps) => {
  const [primary] = specialtyResources;
  if (!primary && careStageResources.length === 0) return null;

  return (
    <div className="space-y-4 rounded-2xl border border-border-subtle bg-card-base p-5">
      <div className="flex items-center gap-1.5">
        <FileText className="h-3.5 w-3.5 text-text-tertiary" aria-hidden />
        <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-text-tertiary">
          Key Resources
        </p>
      </div>

      {primary ? (
        <a
          href={resourceHref(primary.seoUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full rounded-xl border border-border-subtle bg-sage-bg p-4 text-left transition-colors duration-150 ease-out hover:border-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
        >
          <div className="flex items-baseline gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-sage" aria-hidden />
            <h4 className="font-termina font-medium text-sm text-text-primary">
              {primary.title}
            </h4>
          </div>
          {primary.desc ? (
            <p className="mt-2 line-clamp-3 text-xs text-text-secondary">
              {primary.desc}
            </p>
          ) : null}
          <span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-sage transition-colors duration-150 ease-out hover:text-[hsl(169_95%_18%)]">
            Read guide
            <ArrowRight className="h-3 w-3" aria-hidden />
          </span>
        </a>
      ) : null}

      {careStageResources.length > 0 ? (
        <ul className={`${primary ? 'border-t border-border-subtle pt-3' : ''} space-y-1`}>
          {careStageResources.map((resource) => (
            <li key={resource.seoUrl}>
              <a
                href={resourceHref(resource.seoUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center gap-2 rounded-md py-2 text-left text-sm text-text-primary transition-colors duration-150 ease-out hover:text-sage focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
              >
                <FileText
                  className="h-3.5 w-3.5 flex-shrink-0 text-text-tertiary"
                  aria-hidden
                />
                <span className="flex-1 truncate">{resource.title}</span>
                <ArrowRight className="h-3 w-3 text-text-tertiary" aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

// Same path the PDF deep-links to. Origin is implicit (current window) so
// dev / UAT / prod each hit their own resource-hub route automatically.
function resourceHref(seoUrl: string): string {
  return `/resource-hub-detail/${encodeURIComponent(seoUrl)}`;
}
